import { View, Text, Pressable, StyleSheet, Platform } from 'react-native'
import { useRouter, usePathname } from 'expo-router'
import { Shield, LayoutDashboard, Mail, FileText, MessageSquare, Settings, LogOut } from 'lucide-react-native'
import { useCallback } from 'react'
import { useAuth } from '../auth/AuthProvider'
import { storage, TOKEN_KEY, USER_ID_KEY } from '../utils/storage'
import { colors, spacing, borderRadius } from '../theme'

interface DesktopSidebarProps {
  unreadCount: number
}

const NAV_ITEMS = [
  { segment: 'index', label: 'Oversigt', Icon: LayoutDashboard },
  { segment: 'inbox', label: 'Indbakke', Icon: Mail },
  { segment: 'quotes', label: 'Tilbud', Icon: FileText },
  { segment: 'chat', label: 'Chat', Icon: MessageSquare },
  { segment: 'settings', label: 'Indstillinger', Icon: Settings },
]

export default function DesktopSidebar({ unreadCount }: DesktopSidebarProps) {
  const { user, logout } = useAuth()
  const router = useRouter()
  const pathname = usePathname()

  const handleLogout = useCallback(async () => {
    await storage.remove(TOKEN_KEY)
    await storage.remove(USER_ID_KEY)
    logout()
  }, [logout])

  const isActive = (segment: string) => {
    if (segment === 'index') return pathname === '/' || pathname === '/index'
    return pathname === `/${segment}` || pathname?.startsWith(`/${segment}/`)
  }

  return (
    <View style={styles.container}>
      {/* Logo — links to dashboard (home) */}
      <Pressable onPress={() => router.navigate('/(tabs)/' as any)} style={styles.logoRow}>
        <Shield size={28} color={colors.primary} />
        <Text style={styles.logoText}>ForbrugerAgenten</Text>
      </Pressable>

      {/* Agent Email Card removed — users don't need to see/use the agent email */}

      {/* Navigation */}
      <View style={styles.nav}>
        {NAV_ITEMS.map(({ segment, label, Icon }) => {
          const active = isActive(segment)
          return (
            <Pressable
              key={segment}
              onPress={() => {
                const path = segment === 'index' ? '/(tabs)/' : `/(tabs)/${segment}`
                router.navigate(path as any)
              }}
              style={[styles.navItem, active && styles.navItemActive]}
            >
              <Icon size={20} color={active ? '#fff' : colors.text.secondary} />
              <Text style={[styles.navLabel, active && styles.navLabelActive]}>{label}</Text>
              {segment === 'inbox' && unreadCount > 0 && (
                <View style={[styles.badge, active && styles.badgeActive]}>
                  <Text style={styles.badgeText}>
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </Text>
                </View>
              )}
            </Pressable>
          )
        })}
      </View>

      {/* User info + logout */}
      <View style={styles.userSection}>
        <View style={styles.userInfo}>
          <View style={styles.avatar}>
            <Text style={styles.avatarText}>
              {user?.full_name?.[0] || user?.email?.[0] || '?'}
            </Text>
          </View>
          <View style={{ flex: 1, minWidth: 0 }}>
            <Text style={styles.userName} numberOfLines={1}>
              {user?.full_name || 'Bruger'}
            </Text>
            <Text style={styles.userEmail} numberOfLines={1}>{user?.email}</Text>
          </View>
        </View>
        <Pressable onPress={handleLogout} style={styles.logoutBtn}>
          <LogOut size={16} color={colors.error} />
          <Text style={styles.logoutText}>Log ud</Text>
        </Pressable>
      </View>
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    width: 288,
    height: '100%',
    backgroundColor: colors.background,
    borderRightWidth: 1,
    borderRightColor: colors.border,
    ...Platform.select({
      web: { boxShadow: '4px 0 12px rgba(0,0,0,0.08)' } as any,
      ios: { shadowColor: '#000', shadowOffset: { width: 4, height: 0 }, shadowOpacity: 0.08, shadowRadius: 12 },
      android: { elevation: 6 },
    }),
  },
  logoRow: {
    height: 64,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 24,
    borderBottomWidth: 1,
    borderBottomColor: colors.borderLight,
  },
  logoText: {
    fontSize: 18,
    fontWeight: '700',
    color: colors.text.primary,
  },
  nav: {
    padding: 16,
    marginTop: 8,
    gap: 4,
  },
  navItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: borderRadius.md,
  },
  navItemActive: {
    backgroundColor: colors.primary,
  },
  navLabel: {
    flex: 1,
    fontSize: 15,
    fontWeight: '500',
    color: colors.text.secondary,
  },
  navLabelActive: {
    color: '#fff',
  },
  badge: {
    minWidth: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: colors.primary,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 6,
  },
  badgeActive: {
    backgroundColor: 'rgba(255,255,255,0.2)',
  },
  badgeText: {
    fontSize: 11,
    fontWeight: '600',
    color: '#fff',
  },
  userSection: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    padding: 16,
    borderTopWidth: 1,
    borderTopColor: colors.borderLight,
    backgroundColor: colors.surface,
  },
  userInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 12,
  },
  avatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: colors.primary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#fff',
  },
  userName: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.text.primary,
  },
  userEmail: {
    fontSize: 12,
    color: '#6b7280',
  },
  logoutBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
  },
  logoutText: {
    fontSize: 14,
    color: colors.error,
  },
})
